import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Trash2, Save, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Panel, OVERLINE, H1, btnPrimary, btnSecondary, useAdmin } from "@/components/admin/ui";

const ROLE_LABELS = { owner: "Owner", admin: "Admin", support: "Support", content_manager: "Content Manager" };

function AdminsManager() {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [roles, setRoles] = useState(["support"]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.adminAdmins().then(setData).catch(() => setData(false)); }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!email || roles.length === 0) return toast.error("Email and a role are required");
    setBusy(true);
    try { await api.adminAddAdmin({ email: email.trim().toLowerCase(), roles }); toast.success("Admin added"); setEmail(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const toggleRole = (r) => setRoles((rs) => rs.includes(r) ? rs.filter((x) => x !== r) : [...rs, r]);
  const setUserRoles = async (id, newRoles) => { try { await api.adminSetAdminRoles(id, newRoles); toast.success("Roles updated"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const revoke = async (id) => { if (!window.confirm("Revoke admin access for this user?")) return; try { await api.adminRevokeAdmin(id); toast.success("Access revoked"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };

  if (data === false) return null; // not owner
  return (
    <Panel title="Admins & roles" className="mt-6">
      {!data ? <Loader2 className="animate-spin text-[#1F2A22]" /> : (
        <>
          <div className="space-y-2" data-testid="admins-list">
            {data.admins.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center gap-3 border border-[#E5E2DC] px-4 py-3" data-testid="admin-user-row">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1F2A22] truncate">{a.display_name || a.name || a.email}</p>
                  <p className="text-xs text-[#5C6B61] truncate">{a.email}</p>
                </div>
                <div className="flex flex-wrap gap-1">
                  {data.available_roles.map((r) => (
                    <button key={r} onClick={() => setUserRoles(a.id, a.roles.includes(r) ? a.roles.filter((x) => x !== r) : [...a.roles, r])}
                      className={`text-[11px] px-2 py-1 border ${a.roles.includes(r) ? "bg-[#1F2A22] text-[#F9F8F6] border-[#1F2A22]" : "border-[#E5E2DC] text-[#5C6B61]"}`} data-testid={`role-${a.id}-${r}`}>{ROLE_LABELS[r]}</button>
                  ))}
                </div>
                <button onClick={() => revoke(a.id)} className="text-[#D9381E] hover:opacity-70" aria-label="Revoke" data-testid="admin-revoke"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
          <div className="mt-4 border-t border-[#E5E2DC] pt-4">
            <span className={OVERLINE}>Add an admin</span>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="existing user's email" className="border border-[#E5E2DC] bg-white px-3 py-2 text-sm flex-1 min-w-[200px] outline-none focus:border-[#1F2A22]" data-testid="add-admin-email" />
              {data.available_roles.map((r) => (
                <button key={r} onClick={() => toggleRole(r)} className={`text-[11px] px-2 py-1.5 border ${roles.includes(r) ? "bg-[#FF6A00] text-white border-[#FF6A00]" : "border-[#E5E2DC] text-[#5C6B61]"}`} data-testid={`newrole-${r}`}>{ROLE_LABELS[r]}</button>
              ))}
              <button onClick={add} disabled={busy} className={btnPrimary} data-testid="add-admin-submit"><Plus size={14} /> Add</button>
            </div>
            <p className="text-xs text-[#5C6B61] mt-2">The person must already have a HomeRange account. Owners have full access.</p>
          </div>
        </>
      )}
    </Panel>
  );
}

function FeeConfig({ canEdit }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.adminFees().then(setCfg).catch(() => {}); }, []);
  const save = async () => {
    setBusy(true);
    try { await api.adminUpdateFees({ default_pct: Number(cfg.default_pct), promo_pct: cfg.promo_pct === "" ? null : cfg.promo_pct }); toast.success("Fee config saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  if (!cfg) return null;
  return (
    <Panel title="Platform commission" className="mt-6">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className={OVERLINE}>Default marketplace fee (%)</label>
          <input type="number" step="0.1" disabled={!canEdit} value={cfg.default_pct} onChange={(e) => setCfg({ ...cfg, default_pct: e.target.value })}
            className="block w-40 border border-[#E5E2DC] bg-white px-3 py-2 mt-1 text-sm outline-none focus:border-[#1F2A22] disabled:opacity-60" data-testid="fee-default" />
        </div>
        {canEdit && <button onClick={save} disabled={busy} className={btnPrimary} data-testid="fee-save">{busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save</button>}
      </div>
      <p className="text-xs text-[#5C6B61] mt-3">Historical transactions keep the fee that applied when they occurred. Category- and seller-specific overrides are supported by the API and can be surfaced here next.</p>
      {!canEdit && <p className="text-xs text-[#8A5A44] mt-1">Only Owners can change commission rates.</p>}
    </Panel>
  );
}

function AuditLog() {
  const [logs, setLogs] = useState(null);
  useEffect(() => { api.adminAuditLogs({ page: 1, page_size: 30 }).then((d) => setLogs(d.logs)).catch(() => setLogs([])); }, []);
  return (
    <Panel title="Audit log" className="mt-6">
      {logs === null ? <Loader2 className="animate-spin text-[#1F2A22]" /> : logs.length === 0 ? (
        <p className="text-sm text-[#5C6B61]">No administrative actions recorded yet.</p>
      ) : (
        <div className="divide-y divide-[#E5E2DC]" data-testid="audit-log">
          {logs.map((l) => (
            <div key={l.id} className="flex items-center justify-between py-2.5 text-sm">
              <span className="text-[#1F2A22]"><span className="font-mono text-xs bg-[#F2F0EB] px-1.5 py-0.5 mr-2">{l.action}</span>{l.target_type || ""} {l.target_id ? `· ${String(l.target_id).slice(-8)}` : ""}</span>
              <span className="text-xs text-[#5C6B61] whitespace-nowrap">{l.actor_email} · {(l.created_at || "").slice(0, 16).replace("T", " ")}</span>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

export default function AdminSettings() {
  const { admin } = useAdmin();
  const isOwner = admin?.roles?.includes("owner");
  return (
    <div className="p-6 md:p-8" data-testid="admin-settings">
      <span className={OVERLINE}>Operations</span>
      <h1 className={`${H1} mt-1`}>Settings</h1>
      <div className="flex items-center gap-2 mt-3 text-sm text-[#5C6B61]"><ShieldCheck size={16} className="text-[#1F2A22]" /> Signed in as <strong className="text-[#1F2A22]">{admin?.email}</strong> · {(admin?.roles || []).join(", ")}</div>

      {isOwner && <AdminsManager />}
      {admin?.permissions?.includes("finance") && <FeeConfig canEdit={admin?.permissions?.includes("fees_config")} />}
      <AuditLog />
    </div>
  );
}
